import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Calendar, ArrowUpRight } from 'lucide-react';

import SectionHeading from 'components/SectionHeading';
import Button from 'components/Button';

/**
 * Latest News — Graha Prime "Blogs" pattern
 * Bg-dark секц, 3-col card grid
 */
export default function LatestNewsSection({ news = [] }) {
    const { t } = useTranslation();
    const lang = useSelector((s) => s.system.lang || 'mn');

    // API алдаа гарч массив бус (error object) ирвэл ч сайт унахгүй —
    // зүгээр л секцийг харуулахгүй.
    if (!Array.isArray(news) || news.length === 0) return null;

    return (
        <section className="py-20 lg:py-28 bg-white">
            <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
                <SectionHeading
                    eyebrow={lang === 'en' ? 'Latest News' : 'Цаг үеийн мэдээ'}
                    title={
                        lang === 'en'
                            ? "Stay Informed"
                            : 'Мэдээ мэдээлэл'
                    }
                />

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {news.slice(0, 3).map((item) => (
                        <NewsCard key={item.id} item={item} lang={lang} />
                    ))}
                </div>

                <div className="mt-12 flex justify-center">
                    <Button href="/news" variant="outline-dark" size="md">
                        {t('common.viewAll')}
                    </Button>
                </div>
            </div>
        </section>
    );
}

function NewsCard({ item, lang }) {
    const title = lang === 'en' && item.nameEn ? item.nameEn : item.name;
    const description =
        lang === 'en' && item.descriptionEn ? item.descriptionEn : item.description;
    const imageUrl = item.imageUrl || '/placeholders/news-default.svg';

    return (
        <Link
            href={`/news/${item.id}`}
            className="group flex flex-col bg-white overflow-hidden border border-brand-cream-dark hover:border-brand-yellow shadow-sm hover:shadow-lg transition-all duration-300"
        >
            <div className="relative aspect-[16/10] bg-brand-dark overflow-hidden">
                <Image
                    src={imageUrl}
                    alt={title}
                    fill
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="object-cover group-hover:scale-110 transition-transform duration-700"
                />
                {/* Date badge */}
                <div className="absolute top-4 left-4 bg-brand-yellow text-brand-dark px-3 py-1.5 font-heading font-semibold text-xs uppercase tracking-wider">
                    {item.createdAtDay}
                </div>
            </div>
            <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-heading uppercase font-bold text-brand-dark text-lg lg:text-xl leading-tight mb-3 line-clamp-2 group-hover:text-brand-yellow-dark transition">
                    {title}
                </h3>
                {description && (
                    <p className="text-sm text-brand-gray line-clamp-2 leading-relaxed mb-4">
                        {description.replace(/<[^>]+>/g, '')}
                    </p>
                )}
                <div className="mt-auto inline-flex items-center gap-2 text-xs font-heading uppercase font-semibold tracking-wider text-brand-yellow group-hover:gap-3 transition-all">
                    {lang === 'en' ? 'Read More' : 'Үргэлжлүүлэн унших'}
                    <ArrowUpRight
                        size={14}
                        className="group-hover:rotate-45 transition-transform"
                    />
                </div>
            </div>
        </Link>
    );
}